import React, { useState } from 'react';
import type { ArsGloss } from '../../../services/arsClaimService';
import arsClaimService from '../../../services/arsClaimService';

interface Props {
    claimId: number;
    glosses: ArsGloss[];
    onUpdate: () => void;
}

const GlossReviewPanel: React.FC<Props> = ({ claimId, glosses, onUpdate }) => {
    const [disputeForm, setDisputeForm] = useState<Record<number, { amount: string; reason: string }>>({});
    const [loading, setLoading] = useState<Record<number, boolean>>({});

    const handleAccept = async (glossId: number) => {
        setLoading(prev => ({ ...prev, [glossId]: true }));
        try {
            await arsClaimService.acceptGloss(claimId, glossId);
            onUpdate();
        } catch {
            alert('Error al aceptar el gloss');
        } finally {
            setLoading(prev => ({ ...prev, [glossId]: false }));
        }
    };

    const handleDispute = async (glossId: number) => {
        const form = disputeForm[glossId];
        if (!form || !form.amount || !form.reason) {
            alert('Debe ingresar monto disputado y razón');
            return;
        }
        setLoading(prev => ({ ...prev, [glossId]: true }));
        try {
            await arsClaimService.disputeGloss(claimId, glossId, {
                disputed_amount: parseFloat(form.amount),
                resolution_notes: form.reason,
            });
            setDisputeForm(prev => ({ ...prev, [glossId]: { amount: '', reason: '' } }));
            onUpdate();
        } catch {
            alert('Error al disputar el gloss');
        } finally {
            setLoading(prev => ({ ...prev, [glossId]: false }));
        }
    };

    const totalGlossed = glosses.reduce((sum, g) => sum + (g.disputed_amount ?? 0), 0);
    const pendingGlosses = glosses.filter(g => g.status === 'pending').length;

    return (
        <div className="card mb-3">
            <div className="card-body">
                <h5 className="mb-3">
                    Glosses <span className="badge bg-warning ms-2">{pendingGlosses} pendientes</span>
                </h5>

                {glosses.length === 0 && (
                    <p className="text-muted">No hay glosses registrados.</p>
                )}

                {glosses.length > 0 && (
                    <>
                        <div className="table-responsive">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Descripción</th>
                                        <th>Monto Disputado</th>
                                        <th>Estado</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {glosses.map(gloss => (
                                        <tr key={gloss.id}>
                                            <td><code>{gloss.gloss_code}</code></td>
                                            <td>{gloss.gloss_description}</td>
                                            <td>${(gloss.disputed_amount ?? 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                                            <td>
                                                <span className={`badge ${gloss.status === 'pending' ? 'bg-warning text-dark' : gloss.status === 'disputed' ? 'bg-info' : 'bg-success'}`}>
                                                    {gloss.status === 'pending' ? 'Pendiente' : gloss.status === 'disputed' ? 'Disputado' : 'Resuelto'}
                                                </span>
                                            </td>
                                            <td>
                                                {gloss.status === 'pending' && (
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleAccept(gloss.id)}
                                                            disabled={loading[gloss.id]}
                                                        >
                                                            Aceptar
                                                        </button>
                                                        <div className="d-flex gap-1">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                placeholder="Monto"
                                                                style={{ width: 100 }}
                                                                value={disputeForm[gloss.id]?.amount ?? ''}
                                                                onChange={e => setDisputeForm(prev => ({
                                                                    ...prev,
                                                                    [gloss.id]: { amount: e.target.value, reason: prev[gloss.id]?.reason ?? '' },
                                                                }))}
                                                            />
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Razón"
                                                                style={{ width: 150 }}
                                                                value={disputeForm[gloss.id]?.reason ?? ''}
                                                                onChange={e => setDisputeForm(prev => ({
                                                                    ...prev,
                                                                    [gloss.id]: { reason: e.target.value, amount: prev[gloss.id]?.amount ?? '' },
                                                                }))}
                                                            />
                                                            <button
                                                                className="btn btn-sm btn-warning"
                                                                onClick={() => handleDispute(gloss.id)}
                                                                disabled={loading[gloss.id]}
                                                            >
                                                                Disputar
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {gloss.status !== 'pending' && (
                                                    <span className="text-muted small">
                                                        {gloss.resolution_notes}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-2">
                            <strong>Total glossado: </strong>
                            <span className="text-danger">${totalGlossed.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                            <span className="ms-3"><strong>Total disputado: </strong>
                                ${glosses.filter(g => g.status === 'disputed').reduce((s, g) => s + (g.disputed_amount ?? 0), 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default GlossReviewPanel;
